import { useCallback } from "react";
import { loadChatApi, loadSessionApi, loadSharedChatApi, getSessionInfoData, buildTree } from "../../../api";
import { SessionInfo, YearData, DeveloperOptions, DataOptions } from "../../../api/models";
import { AnalysisPanelTabs } from "../../../components/AnalysisPanel";

/**
 * Custom hook for session-related API operations
 */
export const useSessionAPI = () => {
    /**
     * Loads chat data for a specific session
     */
    const loadChatData = useCallback(
        async (
            sessionId: string,
            isSideMenuItemClicked: boolean,
            isChatAPIRequestAborted: boolean,
            setIsHistoryPageInitialLoading: (value: boolean) => void,
            setIsEdited: (value: boolean) => void,
            setIsNewChatThreadInitialLoading: (value: boolean) => void,
            setIsAnswerLoading: (value: boolean) => void,
            setError: (error: unknown) => void,
            setConversationDetails: (details: any) => void,
            setCurrentView: (view: string) => void,
            setnewChatQuestion: (question: string) => void,
            setDocType: (value: boolean) => void,
            setIsOnEdit: (value: boolean) => void,
            setShowSupportLink: (value: boolean) => void,
            setIsSessionNotFound: (value: boolean) => void,
            clearChatState: () => void,
            setSelectedSessionId: (id: string) => void,
            setDeveloperSettings: (settings: DeveloperOptions) => void,
            setOptions: (options: DataOptions) => void,
            currentTargetQuestionId: any,
            setSelectedAnswer: (index: number) => void,
            setScrollToAnswer: (value: boolean) => void,
            setSelectedCitation: (index: number) => void,
            setOverAllCurrentSessionDataPoints: (data: any) => void,
            setAnswers: (answers: any) => void,
            setAllAnswersTree: (tree: any) => void,
            setHistories: (histories: any) => void,
            setVersionIndexMap: (map: any) => void,
            setActiveAnalysisPanelTab: (tab: AnalysisPanelTabs | undefined) => void,
            userName: string,
            selectedAppType: string
        ) => {
            try {
                setIsHistoryPageInitialLoading(true);
                setIsEdited(true);
                setIsNewChatThreadInitialLoading(false);
                setIsAnswerLoading(true);
                setError(undefined);
                setConversationDetails([]);
                setCurrentView("history");
                setnewChatQuestion("");
                setDocType(false);
                setIsOnEdit(false);
                setShowSupportLink(false);

                const response = await loadChatApi(sessionId, "chat");
                const result = await response.json();
                const options = (result?.options ?? {}) as DataOptions;

                if (!result.answers || result.answers.length === 0) {
                    setIsSessionNotFound(true);
                    clearChatState();
                    setSelectedSessionId(sessionId);
                    setDeveloperSettings(result.settings ?? {});
                    setCurrentView("history");
                    setOptions(options);
                    return;
                } else {
                    setIsSessionNotFound(false);
                }

                if (!isChatAPIRequestAborted) {
                    currentTargetQuestionId.current = result.answers.length - 1;
                    setSelectedAnswer(result?.answers?.length > 0 ? result?.answers?.length - 1 : result?.answers?.length);
                    setScrollToAnswer(true);

                    if (Object.keys(options).length > 0) {
                        setOptions(options);
                    }
                }

                for (let i = 0; i < result.answers.length; i++) {
                    setConversationDetails((prevConversationId: any) => [
                        ...prevConversationId,
                        {
                            userName: userName,
                            groupId: sessionId,
                            title: result.answers[i][0]?.extractedQuestion ?? result.answers[i][0]?.question,
                            conversationDate: {
                                Manuals: result.answers[i][1]["Manuals"]?.context.feedback.c_date ?? "NA",
                                FAQ: result.answers[i][1]["FAQ"]?.context.feedback.c_date ?? "NA",
                                ALL: result.answers[i][1]["ALL"]?.context.feedback.c_date ?? "NA"
                            },
                            conversations: {
                                Manuals: result.answers[i][1]["Manuals"]?.context ?? "NA",
                                FAQ: result.answers[i][1]["FAQ"]?.context ?? "NA",
                                ALL: result.answers[i][1]["ALL"]?.context ?? "NA"
                            }
                        }
                    ]);
                }

                setSelectedCitation(0);
                setOverAllCurrentSessionDataPoints(result.answers);
                setAnswers([...result.answers]);
                setAllAnswersTree(buildTree(result.answers, result.histories));
                setHistories(result.histories);
                setVersionIndexMap(Object.fromEntries(Object.keys(result.histories ?? {}).map(id => [id, 0])));
                setSelectedSessionId(sessionId);
                setDeveloperSettings(result.settings);

                if (result?.answers[0] && result?.answers[0][1][selectedAppType]?.context?.data_points?.length > 0) {
                    setActiveAnalysisPanelTab(AnalysisPanelTabs.SupportingContentTab);
                } else {
                    setActiveAnalysisPanelTab(undefined);
                }
            } catch (error) {
                console.error("Error fetching chat data:", error);
                setError(error);
            } finally {
                setIsHistoryPageInitialLoading(false);
                setIsAnswerLoading(false);
                setScrollToAnswer(false);
            }
        },
        []
    );

    /**
     * Loads session list data
     */
    const loadSessionData = useCallback(
        async (
            nextDateToLoad: string,
            searchTerm: string = "",
            isNextLoadClicked: boolean = false,
            setIsSessionLoading: (value: boolean) => void,
            setTodaySessionsRef: (sessions: SessionInfo[]) => void,
            setLast7SessionsRef: (sessions: SessionInfo[]) => void,
            setLast30SessionsRef: (sessions: SessionInfo[]) => void,
            setMonthlySessions: (sessions: YearData[]) => void,
            monthlySessions: YearData[],
            setNextDateToLoad: (date: string) => void
        ) => {
            try {
                setIsSessionLoading(true);
                const response = await loadSessionApi(nextDateToLoad, searchTerm);
                const result = await response.json();

                if (nextDateToLoad === "" || searchTerm) {
                    setTodaySessionsRef(result["today"] ?? []);
                    setLast7SessionsRef(result["7days"] ?? []);
                    setLast30SessionsRef(result["30days"] ?? []);
                }

                if (isNextLoadClicked) {
                    setMonthlySessions(monthlySessions?.length > 0 ? [...monthlySessions, ...result["months"]] : (result["months"] ?? []));
                } else {
                    setMonthlySessions(result["months"] ?? []);
                }

                setNextDateToLoad(result["nextDate"] ?? "");
            } catch (error) {
                console.error("Error fetching session data:", error);
            } finally {
                setIsSessionLoading(false);
            }
        },
        []
    );

    /**
     * Loads shared chat data
     */
    const loadSharedChat = useCallback(
        async (
            sessionvalue: string,
            userCorpId: string,
            userName: string,
            setIsChatShared: (value: boolean) => void,
            setIsSessionNotFound: (value: boolean) => void,
            loadChatDataCallback: (sessionId: string, isSideMenuItemClicked: boolean, isChatAPIRequestAborted: boolean) => Promise<void>,
            setCurrentIndex: (index: string) => void,
            clearChat: () => void
        ) => {
            sessionStorage.setItem("sessionId", sessionvalue);

            try {
                const response = await loadSharedChatApi(sessionvalue, userCorpId, "chat");
                const result = await response.json();
                const options = result?.options ?? {};

                const hasAnswers = !!result && typeof result === "object" && Array.isArray(result.answers) && result.answers.length > 0;
                if (hasAnswers) {
                    setIsSessionNotFound(false);

                    const isOwner = options.userId === userName;
                    setIsChatShared(!isOwner);

                    if (options.shared_chat === true || isOwner) {
                        await loadChatDataCallback(sessionvalue, false, false);
                        setCurrentIndex(sessionvalue ?? "");
                    } else {
                        clearChat();
                    }
                } else {
                    setIsSessionNotFound(true);
                    clearChat();
                }
            } catch (error) {
                console.error("Error fetching shared chat data:", error);
            }
        },
        []
    );

    /**
     * Gets session info for chat summary title retention
     */
    const getSessionInfoForTitleRetain = useCallback(
        async (
            sessionIdvalue: string,
            todaySessionsRef: SessionInfo[],
            setTodaySessionsRef: (sessions: SessionInfo[]) => void,
            setCurrentIndex: (index: string) => void,
            lastActiveIndexRef: any,
            lastSessionRef: any,
            lastActiveSessionRef: any
        ) => {
            try {
                const response = await getSessionInfoData(sessionIdvalue);
                const result = await response.json();

                const indexTarget: number = todaySessionsRef.findIndex((session: SessionInfo) => session.session_id === sessionIdvalue);

                const targetSession = result[String(sessionIdvalue)];
                const todaySessionsRefCopy: SessionInfo[] = [...todaySessionsRef];

                todaySessionsRefCopy[indexTarget] = {
                    title: targetSession.title,
                    conversation_date: targetSession.conversation_date,
                    session_id: sessionIdvalue,
                    corp_id: targetSession.corp_id,
                    user_id: targetSession.user_id,
                    group: targetSession.group,
                    chat_summary_title: targetSession.chat_summary_title
                };

                setTodaySessionsRef(todaySessionsRefCopy);
                setCurrentIndex(sessionStorage.getItem("sessionId") ?? "");
                lastActiveIndexRef.current = targetSession.group;
                lastSessionRef.current = targetSession;
                lastActiveSessionRef.current = todaySessionsRefCopy;
            } catch (error) {
                console.error("Error fetching session info:", error);
            }
        },
        []
    );

    return {
        loadChatData,
        loadSessionData,
        loadSharedChat,
        getSessionInfoForTitleRetain
    };
};
